import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useApi — appelle une fonction async à chaque montage / changement de deps.
 * Retourne { data, loading, error, refetch, setData }
 */
export const useApi = (fetchFn, deps = [], initialData = null) => {
  const [data, setData]       = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (mountedRef.current) setData(result);
    } catch (err) {
      if (mountedRef.current) setError(err.message || 'Erreur inconnue');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { execute(); }, [execute]);

  return { data, loading, error, refetch: execute, setData };
};

/**
 * useMutation — enveloppe une action async (POST/PUT/DELETE).
 * Retourne [mutate, { loading, error, reset }]
 */
export const useMutation = (mutFn) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const reset = useCallback(() => setError(null), []);

  const mutate = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await mutFn(...args);
      return result;
    } catch (err) {
      const msg = err.message || 'Erreur inconnue';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [mutate, { loading, error, reset }];
};
